// @ts-check

import { Wishlist } from '@theme/lib-wishlist';
import { cartFetchAndRender } from '@theme/lib-cart';

/** @type {WeakSet<HTMLButtonElement>} */
var inFlightButtons = new WeakSet();

/**
 * @returns {Set<number>}
 */
function getBusyLinesStore() {
  var windowWithStore =
    /** @type {Window & { __sideCartBusyLines?: Set<number> }} */ (window);
  if (!windowWithStore.__sideCartBusyLines) {
    windowWithStore.__sideCartBusyLines = new Set();
  }
  return windowWithStore.__sideCartBusyLines;
}

/**
 * @param {number} line
 */
function isLineBusy(line) {
  return getBusyLinesStore().has(line);
}

/**
 * @param {number} line
 * @param {boolean} isBusy
 */
function setLineBusyState(line, isBusy) {
  var lineItems = document.querySelectorAll(
    '.side-cart__item[data-line="' + line + '"]'
  );
  if (!lineItems.length) return;

  lineItems.forEach(function (lineItem) {
    if (!(lineItem instanceof HTMLElement)) return;

    lineItem.classList.toggle('is-loading', isBusy);
    if (isBusy) {
      lineItem.setAttribute('aria-busy', 'true');
    } else {
      lineItem.removeAttribute('aria-busy');
    }

    var controls = lineItem.querySelectorAll(
      '.quantity-controls-form button[type="submit"], .delete-button-form .delete-button, .move-to-wishlist-button'
    );
    controls.forEach(function (control) {
      if (!(control instanceof HTMLButtonElement)) return;

      if (isBusy) {
        control.dataset.wasDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        return;
      }

      if (control.dataset.wasDisabled === 'true') {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.disabled = false;
        control.removeAttribute('aria-disabled');
      }
      delete control.dataset.wasDisabled;
    });
  });
}

/**
 * Extract product card metadata from a move-to-wishlist button element.
 * @param {HTMLElement} button
 * @returns {{title?: string, image?: string, price?: string, url?: string}}
 */
function getProductData(button) {
  return {
    title: button.dataset.productTitle || '',
    image: button.dataset.productImage || '',
    price: button.dataset.wishlistProductPrice || '',
    url: button.dataset.productUrl || '',
  };
}

/**
 * @param {HTMLButtonElement} button
 */
async function handleMoveToWishlist(button) {
  if (inFlightButtons.has(button)) return;

  var line = parseInt(button.dataset.line || '', 10);
  var productId = button.dataset.productId;
  if (Number.isNaN(line) || !productId) return;
  if (isLineBusy(line)) return;

  var busyLines = getBusyLinesStore();
  busyLines.add(line);
  setLineBusyState(line, true);
  inFlightButtons.add(button);

  try {
    Wishlist.add(productId, getProductData(button));

    await cartFetchAndRender(
      'cart/change.js',
      {
        line: line,
        quantity: 0,
      },
      {
        action: 'remove',
        context: 'cart',
        lines: [{ id: line, quantity: 0 }],
      }
    );
  } catch (error) {
    console.error('Move to wishlist failed:', error);
  } finally {
    busyLines.delete(line);
    setLineBusyState(line, false);
    inFlightButtons.delete(button);
  }
}

document.addEventListener(
  'click',
  function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var button = target.closest('[data-action="move-to-wishlist"]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    handleMoveToWishlist(button);
  },
  true
);
